import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { FileSpreadsheet, Send, Trash2, Download, ArrowLeft, Plus, Users, Clock } from 'lucide-react';

const STATUS_COLOR = { Draft: 'bg-slate-100 text-slate-500', Forwarded: 'bg-green-100 text-green-700' };

export default function AllocationReportPage() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const canManage = user?.role_label === 'TimeTableManager' || user?.role === 'SuperAdmin';

  const [reports, setReports] = useState([]);
  const [timetables, setTimetables] = useState([]);
  const [showGenerate, setShowGenerate] = useState(false);
  const [weekLabel, setWeekLabel] = useState('');
  const [selectedTT, setSelectedTT] = useState([]);
  const [detail, setDetail] = useState(null);
  const [filterFaculty, setFilterFaculty] = useState('');
  const [filterBatch, setFilterBatch] = useState('');

  const loadReports = useCallback(async () => {
    try {
      const { data } = await api.getAllocationReports();
      setReports(data || []);
    } catch { toast.error('Failed to load allocation reports'); }
  }, []);

  useEffect(() => {
    loadReports();
    if (canManage) {
      api.ttTimetables().then(r => setTimetables(r.data || [])).catch(() => {});
    }
    try { api.markTaskNotificationsRead(); } catch { /* ignore */ }
  }, [loadReports, canManage]);

  // Open via ?open=<id> deep link (from notification bell)
  useEffect(() => {
    const openId = searchParams.get('open');
    if (openId) {
      openDetail(openId);
      setSearchParams({}, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const toggleTT = (id) => {
    setSelectedTT(sel => sel.includes(id) ? sel.filter(x => x !== id) : [...sel, id]);
  };

  const generate = async () => {
    if (!weekLabel.trim()) return toast.error('Enter a week label (e.g. "20th - 25th July")');
    if (selectedTT.length === 0) return toast.error('Select at least one batch timetable');
    try {
      const { data } = await api.generateAllocationReport(weekLabel.trim(), selectedTT);
      toast.success('Allocation report generated');
      setShowGenerate(false); setWeekLabel(''); setSelectedTT([]);
      loadReports();
      openDetail(data._id);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to generate report'); }
  };

  const openDetail = async (id) => {
    try {
      const { data } = await api.getAllocationReport(id);
      setDetail(data); setFilterFaculty(''); setFilterBatch('');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to open report'); }
  };

  const forward = async (id) => {
    if (!window.confirm('Forward this report to Head, Principal & CEO?')) return;
    try {
      await api.forwardAllocationReport(id);
      toast.success('Forwarded to Head, Principal & CEO');
      loadReports();
      if (detail?._id === id) openDetail(id);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to forward'); }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this report?')) return;
    try {
      await api.deleteAllocationReport(id);
      toast.success('Deleted'); setDetail(null); loadReports();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to delete'); }
  };

  const download = async (id, fmt) => {
    try {
      const params = { format: fmt };
      if (filterFaculty) params.faculty_id = filterFaculty;
      if (filterBatch) params.batch = filterBatch;
      const res = await api.exportAllocationReport(id, params);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url; a.download = `Allocation_Report.${fmt === 'pdf' ? 'pdf' : 'xlsx'}`;
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
      toast.success(`${fmt.toUpperCase()} downloaded`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Download failed'); }
  };

  const filteredRows = (detail?.rows || []).filter(r =>
    (!filterFaculty || r.faculty_id === filterFaculty) &&
    (!filterBatch || (r.batches || []).includes(filterBatch))
  );

  // ================= DETAIL VIEW =================
  if (detail) {
    return (
      <div className="max-w-6xl mx-auto px-4 py-6 space-y-5" data-testid="allocation-report-detail">
        <button data-testid="back-to-list" onClick={() => setDetail(null)} className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800">
          <ArrowLeft className="w-4 h-4" /> Back to reports
        </button>
        <div className="flex items-end justify-between flex-wrap gap-3">
          <div>
            <p className="text-xs font-bold uppercase tracking-widest text-violet-600">Weekly Faculty Allocation</p>
            <h1 className="text-2xl font-black tracking-tight text-slate-900" style={{ fontFamily: 'Chivo, sans-serif' }}>{detail.week_label}</h1>
            <div className="flex items-center gap-2 mt-1">
              <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${STATUS_COLOR[detail.status]}`}>{detail.status}</span>
              <span className="text-xs text-slate-400">Batches: {(detail.batches || []).join(', ') || '—'}</span>
            </div>
          </div>
          <div className="flex gap-2">
            {canManage && detail.status === 'Draft' && (
              <Button data-testid="forward-report-btn" onClick={() => forward(detail._id)} className="h-9 bg-violet-600 hover:bg-violet-700 text-white">
                <Send className="w-4 h-4 mr-1.5" />Forward to Head/Principal/CEO
              </Button>
            )}
            {canManage && detail.status === 'Draft' && (
              <Button data-testid="delete-report-btn" onClick={() => remove(detail._id)} variant="ghost" className="h-9 text-red-500 hover:text-red-600">
                <Trash2 className="w-4 h-4" />
              </Button>
            )}
          </div>
        </div>

        <div className="flex flex-wrap items-end gap-2 rounded-lg border border-violet-200 bg-violet-50/40 p-3">
          <div>
            <label className="text-[10px] font-bold uppercase text-slate-400 block">Filter Faculty</label>
            <select data-testid="filter-faculty" value={filterFaculty} onChange={e => setFilterFaculty(e.target.value)} className="h-8 border border-slate-300 rounded-md px-2 text-sm min-w-[160px]">
              <option value="">All faculty</option>
              {(detail.rows || []).map(r => <option key={r.faculty_id} value={r.faculty_id}>{r.faculty_name}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[10px] font-bold uppercase text-slate-400 block">Filter Batch</label>
            <select data-testid="filter-batch" value={filterBatch} onChange={e => setFilterBatch(e.target.value)} className="h-8 border border-slate-300 rounded-md px-2 text-sm min-w-[140px]">
              <option value="">All batches</option>
              {(detail.batches || []).map(b => <option key={b} value={b}>{b}</option>)}
            </select>
          </div>
          <Button data-testid="download-excel-btn" onClick={() => download(detail._id, 'excel')} variant="outline" className="h-8"><Download className="w-4 h-4 mr-1.5" />Excel</Button>
          <Button data-testid="download-pdf-btn" onClick={() => download(detail._id, 'pdf')} variant="outline" className="h-8"><Download className="w-4 h-4 mr-1.5" />PDF</Button>
        </div>

        <div className="overflow-x-auto border border-slate-200 rounded-lg">
          <table className="min-w-full text-sm" data-testid="allocation-table">
            <thead className="bg-slate-900 text-white">
              <tr>
                <th className="p-2.5 text-left font-bold">Faculty</th>
                <th className="p-2.5 text-center font-bold">Sessions</th>
                <th className="p-2.5 text-center font-bold">Hours</th>
                <th className="p-2.5 text-left font-bold">Subjects</th>
                <th className="p-2.5 text-left font-bold">Batches</th>
                <th className="p-2.5 text-center font-bold">Free Periods</th>
                <th className="p-2.5 text-center font-bold">Gaps</th>
              </tr>
            </thead>
            <tbody>
              {filteredRows.map(r => (
                <tr key={r.faculty_id} className="border-t border-slate-100" data-testid={`alloc-row-${r.faculty_id}`}>
                  <td className="p-2.5 font-semibold text-slate-800">{r.faculty_name}</td>
                  <td className="p-2.5 text-center">{r.total_sessions}</td>
                  <td className="p-2.5 text-center font-bold text-violet-600">{r.total_hours}h</td>
                  <td className="p-2.5 text-slate-500">{(r.subjects || []).join(', ') || '—'}</td>
                  <td className="p-2.5 text-slate-500">{(r.batches || []).join(', ') || '—'}</td>
                  <td className="p-2.5 text-center text-slate-500">{r.free_periods}</td>
                  <td className="p-2.5 text-center text-amber-600 font-semibold">{r.gaps}</td>
                </tr>
              ))}
              {filteredRows.length === 0 && (
                <tr><td colSpan={7} className="p-8 text-center text-slate-400">No matching rows.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  // ================= GENERATE MODAL =================
  if (showGenerate) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-6 space-y-5" data-testid="allocation-generate-form">
        <button onClick={() => setShowGenerate(false)} className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800">
          <ArrowLeft className="w-4 h-4" /> Back
        </button>
        <h1 className="text-2xl font-black tracking-tight text-slate-900" style={{ fontFamily: 'Chivo, sans-serif' }}>Generate Weekly Allocation Report</h1>
        <div>
          <label className="text-[10px] font-bold uppercase text-slate-400 block mb-1">Week Label</label>
          <Input data-testid="week-label-input" value={weekLabel} onChange={e => setWeekLabel(e.target.value)} placeholder="e.g. Week 20th - 25th July" className="h-9 max-w-sm" />
        </div>
        <div>
          <label className="text-[10px] font-bold uppercase text-slate-400 block mb-1">Select Batch Timetables for this Week</label>
          <div className="border border-slate-200 rounded-lg divide-y divide-slate-100 max-h-80 overflow-y-auto">
            {timetables.length === 0 && <p className="p-4 text-sm text-slate-400">No timetables found. Build one under the Timetable tab first.</p>}
            {timetables.map(t => (
              <label key={t._id} data-testid={`select-tt-${t._id}`} className="flex items-center gap-3 px-3 py-2.5 hover:bg-slate-50 cursor-pointer">
                <Checkbox checked={selectedTT.includes(t._id)} onCheckedChange={() => toggleTT(t._id)} />
                <div className="flex-1">
                  <span className="text-sm font-semibold text-slate-800">{t.name}</span>
                  <span className="text-xs text-slate-400 ml-2">{(t.entries || []).length} sessions · {t.status}</span>
                </div>
              </label>
            ))}
          </div>
        </div>
        <Button data-testid="generate-report-btn" onClick={generate} className="h-9 bg-violet-600 hover:bg-violet-700 text-white">
          <FileSpreadsheet className="w-4 h-4 mr-1.5" />Generate Report
        </Button>
      </div>
    );
  }

  // ================= LIST VIEW =================
  return (
    <div className="max-w-5xl mx-auto px-4 py-6 space-y-6" data-testid="allocation-report-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <p className="text-xs font-bold uppercase tracking-widest text-violet-600">Scheduling</p>
          <h1 className="text-3xl font-black tracking-tight text-slate-900" style={{ fontFamily: 'Chivo, sans-serif' }}>Faculty Allocation Report</h1>
          <p className="text-sm text-slate-500 mt-1">{canManage ? 'Auto-generated from the timetable — forward to Head, Principal & CEO.' : 'Weekly faculty workload reports shared by the Time Table Manager.'}</p>
        </div>
        {canManage && (
          <Button data-testid="new-allocation-report-btn" onClick={() => setShowGenerate(true)} className="h-9 bg-violet-600 hover:bg-violet-700 text-white">
            <Plus className="w-4 h-4 mr-1.5" />Generate Report
          </Button>
        )}
      </div>

      {reports.length === 0 ? (
        <div className="text-center py-16" data-testid="allocation-report-empty">
          <FileSpreadsheet className="w-10 h-10 text-slate-300 mx-auto mb-3" />
          <p className="text-sm text-slate-500">{canManage ? 'No reports generated yet.' : 'No reports have been shared with you yet.'}</p>
        </div>
      ) : (
        <div className="grid gap-2">
          {reports.map(r => (
            <button key={r._id} data-testid={`alloc-report-row-${r._id}`} onClick={() => openDetail(r._id)}
              className="flex items-center justify-between gap-3 rounded-lg border border-slate-200 bg-white p-3.5 text-left hover:border-violet-300 transition-colors">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-bold text-slate-800">{r.week_label}</span>
                  <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${STATUS_COLOR[r.status]}`}>{r.status}</span>
                </div>
                <div className="flex items-center gap-3 text-[11px] text-slate-400 mt-0.5">
                  <span className="flex items-center gap-1"><Users className="w-3 h-3" />{(r.batches || []).join(', ') || '—'}</span>
                  <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{r.generated_at ? new Date(r.generated_at).toLocaleDateString() : ''}</span>
                </div>
              </div>
              <Download className="w-4 h-4 text-slate-300 shrink-0" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
